import React, {useState} from 'react';

import {addToast, Button, Dialogue, Icon, Menu, Modal, Text} from '@albert-io/atomic';
import type {IconProp} from '@albert-io/atomic/atoms/Icon/Icon.react';
import {normalizeError} from 'lib/normalizeError';

import type {MutationResult, SettingsClassroom} from './useClassSettings';

type ActionKey = 'archive' | 'unarchive' | 'delete' | 'removeSelf';

interface ActionConfig {
  ariaLabel: string;
  icon: IconProp;
  iconColor: 'primary' | 'negative';
  title: React.ReactNode;
  body?: React.ReactNode;
  confirmLabel: string;
  confirmColor: 'primary' | 'negative';
  successMessage?: string;
  errorMessage: string;
  run: () => Promise<MutationResult>;
}

interface Props {
  classroom: SettingsClassroom;
  onArchive: () => Promise<MutationResult>;
  onUnarchive: () => Promise<MutationResult>;
  onDelete: () => Promise<MutationResult>;
  onRemoveSelf: () => Promise<MutationResult>;
}

export const SettingsActionsMenu = ({
  classroom,
  onArchive,
  onUnarchive,
  onDelete,
  onRemoveSelf
}: Props) => {
  const [openAction, setOpenAction] = useState<ActionKey | null>(null);

  const isArchived = classroom.isArchived();
  const isSoleTeacher = classroom.getTeachers().size === 1;
  const name = classroom.getName();

  const actions: Record<ActionKey, ActionConfig> = {
    archive: {
      ariaLabel: 'Archive classroom',
      icon: 'archive',
      iconColor: 'primary',
      title: <>Are you sure you want to archive “{name}”?</>,
      body: (
        <ul className='mb-x4 gap-x1 flex flex-col'>
          <li>Classroom statistics will still remain available to you.</li>
          <li>Questions in active assignments will remain blocked for students.</li>
          <li>Students and co-teachers will no longer have access.</li>
        </ul>
      ),
      confirmLabel: 'Archive Classroom',
      confirmColor: 'primary',
      successMessage: 'Your classroom has been archived',
      errorMessage: 'There was a problem archiving your classroom',
      run: onArchive
    },
    unarchive: {
      ariaLabel: 'Unarchive classroom',
      icon: 'archive',
      iconColor: 'primary',
      title: <>Unarchive “{name}”?</>,
      body: (
        <Text className='mb-x4 block text-center'>
          Unarchiving a class will make it accessible to students and any co-teachers.
        </Text>
      ),
      confirmLabel: 'Unarchive Classroom',
      confirmColor: 'primary',
      successMessage: 'Your classroom has been made active',
      errorMessage: 'There was a problem making your classroom active',
      run: onUnarchive
    },
    delete: {
      ariaLabel: 'Delete classroom',
      icon: 'octagon-exclamation',
      iconColor: 'negative',
      title: <>Are you sure you want to delete “{name}”?</>,
      confirmLabel: 'Delete Classroom',
      confirmColor: 'negative',
      // Delete redirects home on success, so only the failure path needs a toast.
      errorMessage: 'There was a problem deleting your classroom',
      run: onDelete
    },
    removeSelf: {
      ariaLabel: 'Remove yourself from classroom',
      icon: 'octagon-exclamation',
      iconColor: 'negative',
      title: <>Are you sure you want to remove yourself from “{name}”?</>,
      confirmLabel: 'Remove Me From This Classroom',
      confirmColor: 'negative',
      errorMessage: 'There was a problem removing yourself from this classroom',
      run: onRemoveSelf
    }
  };

  const active = openAction ? actions[openAction] : null;

  const confirm = async (config: ActionConfig) => {
    setOpenAction(null);
    const {ok, error} = await config.run();
    if (ok && config.successMessage) {
      addToast({color: 'positive', title: 'Success', message: config.successMessage});
    } else if (!ok) {
      addToast({
        color: 'negative',
        title: 'Error',
        message: error ? normalizeError(error).message : config.errorMessage
      });
    }
  };

  return (
    <>
      <Menu closeOnItemClick position='bottom-end' positionFixed>
        <Menu.Trigger>
          {(triggerProps) => (
            <Button {...triggerProps} variant='outlined' color='secondary'>
              Options
              <Icon className='ml-x1' icon='caret-down' />
            </Button>
          )}
        </Menu.Trigger>
        <Menu.Content>
          <Menu.Item onClick={() => setOpenAction(isArchived ? 'unarchive' : 'archive')}>
            {isArchived ? 'Unarchive class' : 'Archive class'}
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item
            onClick={() => setOpenAction(isSoleTeacher ? 'delete' : 'removeSelf')}
            textProps={{color: 'negative'}}
          >
            {isSoleTeacher ? 'Delete Classroom' : 'Remove Me from This Classroom'}
          </Menu.Item>
        </Menu.Content>
      </Menu>
      {active && (
        <Modal ariaLabel={active.ariaLabel} handleClose={() => setOpenAction(null)}>
          {({modalContentStyle, CloseButtonWrapper}) => (
            <Dialogue inModal hideCloseBtn className={modalContentStyle} size='s'>
              <Dialogue.Title align='center'>
                <Icon
                  icon={active.icon}
                  iconStyle='light'
                  color={active.iconColor}
                  size='standalone'
                />
              </Dialogue.Title>
              <Dialogue.Body>
                <Text bold className='mb-x3 block text-center'>
                  {active.title}
                </Text>
                {active.body}
              </Dialogue.Body>
              <Button
                variant='outlined'
                color={active.confirmColor}
                className='mb-x1 block w-full'
                onClick={() => confirm(active)}
              >
                {active.confirmLabel}
              </Button>
              <CloseButtonWrapper>
                <Button variant='outlined' color='secondary' className='block w-full'>
                  Cancel
                </Button>
              </CloseButtonWrapper>
            </Dialogue>
          )}
        </Modal>
      )}
    </>
  );
};
